import React, { useState, useMemo } from 'react';
import { Check, X as XIcon, Edit2, Trash2, Plus, Sparkles, Clock, AlertTriangle } from 'lucide-react';
import { useProximoPassoItems } from '../hooks/useProximoPassoItems';
import CollapsibleSection from './CollapsibleSection';
import { formatDate } from '../utils/dateHelpers';

const PRIORIDADE_OPTIONS = [
  { value: 'alta', label: 'Alta' },
  { value: 'media', label: 'Média' },
  { value: 'baixa', label: 'Baixa' },
];

const isPrazoExpirado = (prazo) => {
  if (!prazo) return false;
  const m = String(prazo).match(/(\d{2})\/(\d{2})\/(\d{4})/) || String(prazo).match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return false;
  const d = m[0].includes('/')
    ? new Date(`${m[3]}-${m[2]}-${m[1]}T23:59:59`)
    : new Date(`${m[1]}-${m[2]}-${m[3]}T23:59:59`);
  return d.getTime() < Date.now();
};

const ProximoPassoItem = ({ item, userEmail, onUpdateStatus, onEdit, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editAcao, setEditAcao] = useState(item.acao);
  const [editPrazo, setEditPrazo] = useState(item.prazo_sugerido || '');
  const [editPrioridade, setEditPrioridade] = useState(item.prioridade || 'media');
  const [busy, setBusy] = useState(false);

  const isAgent = item.autor_tipo === 'agent';
  const isFeito = item.status === 'feito';
  const isRejeitado = item.status === 'rejeitado';
  const isEmAndamento = item.status === 'em_andamento';
  const isPendente = item.status === 'pendente';
  const concluido = isFeito || isRejeitado;

  const borderClass = isAgent ? 'border-l-4 border-l-violet-400' : 'border-l-4 border-l-orange-500';
  const bgClass = isFeito
    ? 'bg-emerald-50/30'
    : isRejeitado
      ? 'bg-red-50/20'
      : isEmAndamento
        ? 'bg-blue-50/30'
        : 'bg-white';
  const textClass = concluido ? 'line-through text-slate-500' : 'text-slate-800';
  const opacityClass = concluido ? 'opacity-70' : '';
  const prazoExpirado = isPendente && isPrazoExpirado(item.prazo_sugerido);

  const handleSaveEdit = async () => {
    if (!editAcao.trim()) return;
    setBusy(true);
    await onEdit(item.id, {
      acao: editAcao.trim(),
      prazo_sugerido: editPrazo.trim() || null,
      prioridade: editPrioridade,
    });
    setBusy(false);
    setIsEditing(false);
  };

  const handleStatus = async (newStatus) => {
    setBusy(true);
    await onUpdateStatus(item.id, newStatus, userEmail);
    setBusy(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Remover esta ação?')) return;
    setBusy(true);
    await onDelete(item.id);
  };

  return (
    <div className={`rounded p-3 border border-slate-200 ${borderClass} ${bgClass} ${opacityClass} transition-all`}>
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 mt-0.5">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${
            isAgent ? 'bg-violet-100 text-violet-600' : 'bg-orange-100 text-orange-700'
          }`}>
            {isAgent ? '🤖' : '📝'}
          </div>
        </div>

        <div className="flex-grow min-w-0">
          {isEditing ? (
            <div className="space-y-2">
              <textarea
                value={editAcao}
                onChange={(e) => setEditAcao(e.target.value)}
                rows={3}
                className="w-full text-sm border border-slate-300 rounded px-2 py-1 focus:ring-2 focus:ring-orange-500 outline-none"
                autoFocus
              />
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editPrazo}
                  onChange={(e) => setEditPrazo(e.target.value)}
                  placeholder="Prazo (ex: até 22/04/2026 ou Imediato)"
                  className="flex-grow text-xs border border-slate-300 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-orange-500"
                />
                <select
                  value={editPrioridade}
                  onChange={(e) => setEditPrioridade(e.target.value)}
                  className="text-xs border border-slate-300 rounded px-2 py-1 outline-none"
                >
                  {PRIORIDADE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSaveEdit}
                  disabled={busy || !editAcao.trim()}
                  className="text-xs px-2 py-1 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
                >
                  {busy ? 'Salvando...' : 'Salvar'}
                </button>
                <button
                  onClick={() => { setIsEditing(false); setEditAcao(item.acao); setEditPrazo(item.prazo_sugerido || ''); setEditPrioridade(item.prioridade || 'media'); }}
                  disabled={busy}
                  className="text-xs px-2 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className={`text-sm leading-relaxed whitespace-pre-wrap ${textClass}`}>
                {item.acao}
              </div>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[11px]">
                {item.prioridade === 'alta' && !concluido && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium">
                    <AlertTriangle size={9} /> Alta
                  </span>
                )}
                {isEmAndamento && (
                  <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">em andamento</span>
                )}
                {item.prazo_sugerido && (
                  <span className={`inline-flex items-center gap-0.5 ${prazoExpirado ? 'text-red-600 font-medium' : 'text-orange-600'}`}>
                    <Clock size={10} /> {item.prazo_sugerido}
                  </span>
                )}
                <span className="text-slate-400">·</span>
                <span className="text-slate-500">
                  {item.autor_id || (isAgent ? 'agent' : 'usuário')} · {formatDate(item.created_at)}
                </span>
                {concluido && item.status_changed_at && (
                  <>
                    <span className="text-slate-400">·</span>
                    <span className={isFeito ? 'text-emerald-600' : 'text-red-600'}>
                      {isFeito ? '✓ feito' : '✕ rejeitado'} em {formatDate(item.status_changed_at)}
                    </span>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {!isEditing && (
          <div className="flex-shrink-0 flex items-center gap-1">
            {!concluido && (
              <>
                <button
                  onClick={() => handleStatus('feito')}
                  disabled={busy || !userEmail}
                  className="p-1 text-emerald-600 hover:bg-emerald-100 rounded disabled:opacity-30"
                  title="Marcar como feito"
                >
                  <Check size={14} />
                </button>
                <button
                  onClick={() => handleStatus('rejeitado')}
                  disabled={busy || !userEmail}
                  className="p-1 text-slate-500 hover:bg-red-100 hover:text-red-600 rounded disabled:opacity-30"
                  title="Rejeitar"
                >
                  <XIcon size={14} />
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  disabled={busy}
                  className="p-1 text-slate-500 hover:bg-slate-100 rounded disabled:opacity-30"
                  title="Editar"
                >
                  <Edit2 size={12} />
                </button>
              </>
            )}
            {!isAgent && (
              <button
                onClick={handleDelete}
                disabled={busy}
                className="p-1 text-slate-400 hover:bg-red-100 hover:text-red-600 rounded disabled:opacity-30"
                title="Remover"
              >
                <Trash2 size={12} />
              </button>
            )}
            {concluido && (
              <button
                onClick={() => handleStatus('pendente')}
                disabled={busy || !userEmail}
                className="text-[10px] px-1.5 py-0.5 text-slate-500 hover:text-slate-700 hover:underline"
                title="Voltar para pendente"
              >
                reabrir
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ProximoPassoList = ({ ofertaId, userEmail }) => {
  const { pendentes, concluidos, loading, addItem, updateStatus, editItem, deleteItem } = useProximoPassoItems(ofertaId);
  const [novaAcao, setNovaAcao] = useState('');
  const [novoPrazo, setNovoPrazo] = useState('');
  const [novaPrioridade, setNovaPrioridade] = useState('media');
  const [adding, setAdding] = useState(false);

  const podeAdicionar = useMemo(() => Boolean(userEmail) && novaAcao.trim().length > 0, [userEmail, novaAcao]);

  const handleAdd = async () => {
    if (!podeAdicionar) return;
    setAdding(true);
    const { error } = await addItem({
      acao: novaAcao,
      prazo_sugerido: novoPrazo,
      prioridade: novaPrioridade,
      autor_id: userEmail,
    });
    if (!error) {
      setNovaAcao('');
      setNovoPrazo('');
      setNovaPrioridade('media');
    } else {
      window.alert('Erro ao adicionar ação: ' + (error.message || 'desconhecido'));
    }
    setAdding(false);
  };

  return (
    <div className="space-y-3">
      {/* Form adicionar */}
      <div className="border border-dashed border-slate-300 rounded p-3 bg-slate-50/50">
        <div className="flex items-start gap-2">
          <textarea
            value={novaAcao}
            onChange={(e) => setNovaAcao(e.target.value)}
            placeholder="Nova ação… (ex: Ligar para Marcos Gavião sobre estudo numérico)"
            rows={2}
            className="flex-grow text-sm border border-slate-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-orange-500 outline-none resize-y"
          />
          <button
            onClick={handleAdd}
            disabled={!podeAdicionar || adding}
            className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:opacity-50"
            title={!userEmail ? 'Faça login para adicionar' : 'Adicionar ação'}
          >
            <Plus size={14} /> {adding ? 'Adicionando…' : 'Adicionar'}
          </button>
        </div>
        <div className="mt-2 flex items-center gap-2 text-xs">
          <input
            type="text"
            value={novoPrazo}
            onChange={(e) => setNovoPrazo(e.target.value)}
            placeholder="Prazo (opcional, ex: até 25/04/2026)"
            className="flex-grow border border-slate-300 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-orange-500"
          />
          <select
            value={novaPrioridade}
            onChange={(e) => setNovaPrioridade(e.target.value)}
            className="border border-slate-300 rounded px-2 py-1 outline-none"
          >
            {PRIORIDADE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>Prioridade: {o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Lista pendentes */}
      {loading && (
        <div className="text-xs text-slate-500 italic">Carregando ações…</div>
      )}

      {!loading && pendentes.length === 0 && (
        <div className="text-xs text-slate-500 italic bg-slate-50 border border-dashed border-slate-200 rounded p-3">
          Nenhuma ação pendente. <Sparkles size={11} className="inline text-purple-500" /> O agent propõe novas ações ao gerar o próximo briefing.
        </div>
      )}

      {!loading && pendentes.length > 0 && (
        <div className="space-y-2">
          {pendentes.map((item) => (
            <ProximoPassoItem
              key={item.id}
              item={item}
              userEmail={userEmail}
              onUpdateStatus={updateStatus}
              onEdit={editItem}
              onDelete={deleteItem}
            />
          ))}
        </div>
      )}

      {/* Concluidos colapsavel */}
      {!loading && concluidos.length > 0 && (
        <CollapsibleSection
          title={`Concluídos (${concluidos.length})`}
          defaultExpanded={false}
          icon="📦"
          className=""
        >
          <div className="space-y-2">
            {concluidos.map((item) => (
              <ProximoPassoItem
                key={item.id}
                item={item}
                userEmail={userEmail}
                onUpdateStatus={updateStatus}
                onEdit={editItem}
                onDelete={deleteItem}
              />
            ))}
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
};

export default ProximoPassoList;
